import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { AnimatePresence } from "../../AnimatePresenceWrapper";
import {
  MapPin,
  Globe,
  Phone,
  Mail,
  Calendar,
  Users,
  Building2,
  Award,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Target,
  Eye,
  Heart,
  ArrowRight,
  X,
  Info,
  Ticket,
} from "lucide-react";
import { IOrganizationProps } from "../../../types/Organization";
import { IEventProps, ISmallEventProps } from "../../../types/Event";

import { IThemeProps } from "../../../types/Theme";
import { getEventUrl } from "../../../lib/utils/domain";
import "./styles.scss";

interface OrganizationHomepageThemeTwoProps {
  organization: IOrganizationProps;
  events: ISmallEventProps[];
  theme: IThemeProps | null;
  onGetTicket: (event: ISmallEventProps) => void;
}

const OrganizationHomepageThemeTwo: React.FC<
  OrganizationHomepageThemeTwoProps
> = ({ organization, events: attendeeEvents, theme, onGetTicket }) => {
  const [selectedEvent, setSelectedEvent] = useState<IEventProps | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const primaryColor = theme?.primary_color ?? "#17265a";
  const secondaryColor = theme?.secondary_color ?? "#FFFFFF";

  const events = useMemo<IEventProps[]>(() => {
    return attendeeEvents.map((event) => {
      const baseLocation = {
        id: Number(event.id) || 0,
        address: "",
        city: "",
        state: "",
        country: "",
        postal_code: "",
        latitude: "",
        longitude: "",
        video_conference_link: event.type === "online" ? "" : "",
        video_conference_platform: "",
        video_conference_id: "",
        video_conference_password: "",
      };

      const baseUser = {
        id: organization?.id ?? 0,
        first_name: organization?.name ?? "",
        last_name: "",
        phone_number: null,
        phone_verified: false,
        email: organization?.email ?? "",
        email_verified: true,
        referral_code: "",
        fcm_id: null,
        is_active: true,
        date_joined: "",
        last_login: "",
      };

      return {
        id: Number(event.id),
        user: baseUser,
        organization: null,
        name: event.name,
        date: event.date,
        type: event.type,
        is_paid_event: false,
        cover_image: event.cover_image || "",
        host_name: organization?.name ?? "",
        organizer_description: organization?.description ?? "",
        event_description: event.short_description ?? "",
        number_of_seats: event.total_tickets ?? 0,
        event_attendees_count: event.tickets_sold ?? 0,
        location: baseLocation,
        created_at: "",
        updated_at: "",
      } as IEventProps;
    });
  }, [attendeeEvents, organization]);

  const eventSourceMap = useMemo(() => {
    const map = new Map<number, ISmallEventProps>();
    attendeeEvents.forEach((event) => {
      map.set(Number(event.id), event);
    });
    return map;
  }, [attendeeEvents]);

  const isLoadingEvents = false;

  // All hooks must be called before any early returns
  const socialLinks = useMemo(() => {
    if (!organization) return [];
    return [
      { icon: Facebook, url: organization.facebook_link, label: "Facebook" },
      {
        icon: Instagram,
        url: organization.instagram_link,
        label: "Instagram",
      },
      { icon: Linkedin, url: organization.linkedin_link, label: "LinkedIn" },
      { icon: Twitter, url: organization.x_link, label: "Twitter" },
      { icon: Youtube, url: organization.youtube_link, label: "YouTube" },
    ].filter((link) => link.url);
  }, [organization]);

  const stats = useMemo(() => {
    if (!organization) return [];
    return [
      {
        icon: Calendar,
        value: (organization as any).event_count?.toLocaleString() || "0",
        label: "Events",
      },
      {
        icon: Users,
        value: `${
          (organization as any).event_attendees_count?.toLocaleString() || "0"
        }+`,
        label: "Attendees",
      },
      {
        icon: Building2,
        value: organization.number_of_locations?.toString() || "0",
        label: "Locations",
      },
    ];
  }, [organization]);

  const aboutValues = useMemo(() => {
    if (!organization) return [];
    const about = organization.about as any;
    return [
      {
        icon: Target,
        title: "Mission",
        description: about?.mission || "",
      },
      {
        icon: Eye,
        title: "Vision",
        description: about?.vision || "",
      },
      {
        icon: Heart,
        title: "Values",
        description: about?.values || "",
      },
    ].filter((value) => value.description);
  }, [organization]);

  const locationString = useMemo(() => {
    if (!organization?.location) return "";
    const { address, city, state, country } = organization.location;
    return [address, city, state, country].filter(Boolean).join(", ");
  }, [organization]);

  if (!organization) {
    return (
      <div className="homepage-theme-two-container">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            backgroundColor: secondaryColor,
          }}
        >
          <span style={{ color: primaryColor, fontWeight: 600 }}>
            Loading organization...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="homepage-theme-two-container"
      style={
        {
          "--theme-primary-color": primaryColor,
          "--theme-secondary-color": secondaryColor,
        } as React.CSSProperties
      }
    >
      {/* Hero Section */}
      <section className="theme-two-hero">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-shapes">
            <motion.div
              className="hero-shape hero-shape-1"
              animate={{
                y: [0, -20, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="hero-shape hero-shape-2"
              animate={{
                y: [0, 30, 0],
                rotate: [0, -5, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1,
              }}
            />
            <motion.div
              className="hero-shape hero-shape-3"
              animate={{
                y: [0, -15, 0],
                rotate: [0, 3, 0],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2,
              }}
            />
          </div>
        </div>

        <div className="hero-content">
          <div className="hero-main">
            <motion.div
              className="hero-logo-wrapper"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              {organization.logo ? (
                <motion.img
                  src={organization.logo}
                  alt={organization.name}
                  className="hero-logo"
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ duration: 0.3 }}
                />
              ) : (
                <motion.div
                  className="hero-logo-placeholder"
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ duration: 0.3 }}
                >
                  {organization.name.charAt(0).toUpperCase()}
                </motion.div>
              )}
            </motion.div>

            <motion.div
              className="hero-text"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.h1
                className="hero-title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                {organization.name}
              </motion.h1>
              {organization.subtitle && (
                <motion.p
                  className="hero-subtitle"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {organization.subtitle}
                </motion.p>
              )}
              {organization.description && (
                <motion.p
                  className="hero-description"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  {organization.description.length > 200
                    ? `${organization.description.substring(0, 200)}...`
                    : organization.description}
                </motion.p>
              )}
            </motion.div>
          </div>

          {/* Quick Stats in Hero */}
          {stats.length > 0 && (
            <motion.div
              className="hero-stats"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    className="hero-stat-item"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                  >
                    <div className="hero-stat-icon-wrapper">
                      <Icon className="hero-stat-icon" />
                    </div>
                    <div className="hero-stat-content">
                      <div className="hero-stat-value">{stat.value}</div>
                      <div className="hero-stat-label">{stat.label}</div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* Contact Quick Links */}
          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            {organization.website && (
              <motion.a
                href={organization.website}
                target="_blank"
                rel="noopener noreferrer"
                className="hero-action-btn hero-action-primary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Globe className="hero-action-icon" />
                <span>Visit Website</span>
              </motion.a>
            )}
            {organization.website && (
              <motion.a
                href={organization.website}
                className="hero-action-btn hero-action-secondary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Mail className="hero-action-icon" />
                <span>Visit Site</span>
              </motion.a>
            )}
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      {(organization.about as any)?.about || aboutValues.length > 0 ? (
        <section className="theme-two-about">
          <div className="about-background-decoration">
            <div className="about-decoration-shape about-decoration-1"></div>
            <div className="about-decoration-shape about-decoration-2"></div>
          </div>
          <div className="about-container">
            {(organization.about as any)?.about && (
              <motion.div
                className="about-intro"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  className="about-title-wrapper"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="about-title-accent"></div>
                  <h2 className="about-title">About Us</h2>
                </motion.div>
                <motion.div
                  className="about-text-wrapper"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <p className="about-text">
                    {(organization.about as any).about}
                  </p>
                </motion.div>
              </motion.div>
            )}

            {aboutValues.length > 0 && (
              <motion.div
                className="about-values"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                {aboutValues.map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <motion.div
                      key={index}
                      className="value-card"
                      initial={{ opacity: 0, y: 50, rotateX: -15 }}
                      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{
                        duration: 0.8,
                        delay: index * 0.15,
                        ease: "easeOut",
                      }}
                      whileHover={{ y: -12, scale: 1.02 }}
                    >
                      <div className="value-card-inner">
                        <motion.div
                          className="value-icon-wrapper"
                          whileHover={{ rotate: 360, scale: 1.1 }}
                          transition={{ duration: 0.6, ease: "easeInOut" }}
                        >
                          <div className="value-icon-background"></div>
                          <Icon className="value-icon" />
                          <div className="value-icon-glow"></div>
                        </motion.div>
                        <div className="value-content">
                          <h3 className="value-title">{value.title}</h3>
                          <div className="value-divider"></div>
                          <p className="value-description">
                            {value.description}
                          </p>
                        </div>
                        <div className="value-card-accent"></div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </div>
        </section>
      ) : null}

      {/* Location Section */}
      {organization.location && (
        <section className="theme-two-location">
          <div className="location-background">
            <div className="location-map-pattern"></div>
            <div className="location-decoration-shape location-dec-1"></div>
            <div className="location-decoration-shape location-dec-2"></div>
          </div>
          <div className="location-container">
            <motion.div
              className="location-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="location-icon-wrapper"
                initial={{ opacity: 0, scale: 0.8, rotate: -180 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <MapPin className="location-header-icon" />
                <div className="location-icon-pulse"></div>
              </motion.div>
              <div className="location-header-text">
                <h2 className="location-title">Our Location</h2>
                <div className="location-title-underline"></div>
              </div>
            </motion.div>

            <motion.div
              className="location-content"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="location-main-card">
                <div className="location-main-icon">
                  <Globe className="location-globe-icon" />
                </div>
                {locationString && (
                  <div className="location-full-address">
                    <p className="location-address-text">{locationString}</p>
                  </div>
                )}
              </div>

              <div className="location-details-grid">
                {organization.location.address && (
                  <motion.div
                    className="location-detail-card"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                  >
                    <div className="location-detail-icon-wrapper">
                      <Building2 className="location-detail-icon" />
                    </div>
                    <div className="location-detail-content">
                      <span className="location-detail-label">Address</span>
                      <span className="location-detail-value">
                        {organization.location.address}
                      </span>
                    </div>
                  </motion.div>
                )}

                {organization.location.city && (
                  <motion.div
                    className="location-detail-card"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                  >
                    <div className="location-detail-icon-wrapper">
                      <MapPin className="location-detail-icon" />
                    </div>
                    <div className="location-detail-content">
                      <span className="location-detail-label">City</span>
                      <span className="location-detail-value">
                        {organization.location.city}
                      </span>
                    </div>
                  </motion.div>
                )}

                {organization.location.state && (
                  <motion.div
                    className="location-detail-card"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                  >
                    <div className="location-detail-icon-wrapper">
                      <Award className="location-detail-icon" />
                    </div>
                    <div className="location-detail-content">
                      <span className="location-detail-label">State</span>
                      <span className="location-detail-value">
                        {organization.location.state}
                      </span>
                    </div>
                  </motion.div>
                )}

                {organization.location.country && (
                  <motion.div
                    className="location-detail-card"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                  >
                    <div className="location-detail-icon-wrapper">
                      <Globe className="location-detail-icon" />
                    </div>
                    <div className="location-detail-content">
                      <span className="location-detail-label">Country</span>
                      <span className="location-detail-value">
                        {organization.location.country}
                      </span>
                    </div>
                  </motion.div>
                )}

                {organization.location.postal_code && (
                  <motion.div
                    className="location-detail-card"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.7 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                  >
                    <div className="location-detail-icon-wrapper">
                      <Mail className="location-detail-icon" />
                    </div>
                    <div className="location-detail-content">
                      <span className="location-detail-label">Postal Code</span>
                      <span className="location-detail-value">
                        {organization.location.postal_code}
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Events Section */}
      <section className="theme-two-events">
        <div className="events-container">
          <motion.div
            className="events-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Calendar className="events-header-icon" />
            <h2 className="events-title">Our Events</h2>
          </motion.div>

          {events.length > 0 ? (
            <div className="events-grid">
              {events.slice(0, 6).map((event, index) => {
                const eventDate = new Date(event.date);
                const formattedDate = eventDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });
                const formattedYear = eventDate.getFullYear();
                const formattedTime = eventDate.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                });

                return (
                  <motion.div
                    key={event.id}
                    className="event-card"
                    initial={{ opacity: 0, y: 50, rotateX: -10 }}
                    whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{
                      duration: 0.8,
                      delay: index * 0.1,
                      ease: "easeOut",
                    }}
                    whileHover={{ y: -12, scale: 1.02 }}
                  >
                    <div className="event-card-inner">
                      {/* Image Section */}
                      <div className="event-image-wrapper">
                        {event.cover_image ? (
                          <>
                            <img
                              src={event.cover_image}
                              alt={event.name}
                              className="event-image"
                            />
                            <div className="event-image-overlay"></div>
                          </>
                        ) : (
                          <div className="event-image-placeholder">
                            <Calendar className="event-placeholder-icon" />
                          </div>
                        )}

                        {/* Badges Overlay */}
                        <div className="event-badges-overlay">
                          <motion.div
                            className={`event-pricing-badge ${
                              event.is_paid_event ? "paid" : "free"
                            }`}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 + 0.3 }}
                            whileHover={{ scale: 1.1 }}
                          >
                            <Ticket className="event-badge-icon" />
                            <span>{event.is_paid_event ? "Paid" : "Free"}</span>
                          </motion.div>
                          <motion.div
                            className="event-type-badge"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 + 0.4 }}
                          >
                            {event.type}
                          </motion.div>
                        </div>

                        {/* Date Badge */}
                        <motion.div
                          className="event-date-badge"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 + 0.2 }}
                        >
                          <div className="event-date-day">{formattedDate}</div>
                          <div className="event-date-year">{formattedYear}</div>
                        </motion.div>
                      </div>

                      {/* Content Section */}
                      <div className="event-content">
                        <div className="event-header">
                          <h3 className="event-title">{event.name}</h3>
                          {event.event_description && (
                            <p className="event-description">
                              {event.event_description.length > 120
                                ? `${event.event_description.substring(
                                    0,
                                    120
                                  )}...`
                                : event.event_description}
                            </p>
                          )}
                        </div>

                        <div className="event-details">
                          {event.location && (
                            <motion.div
                              className="event-detail-item"
                              whileHover={{ x: 4 }}
                            >
                              <div className="event-detail-icon-wrapper">
                                <MapPin className="event-detail-icon" />
                              </div>
                              <div className="event-detail-content">
                                <span className="event-detail-label">
                                  Location
                                </span>
                                <span className="event-detail-value">
                                  {event.location.city ||
                                    event.location.address ||
                                    "TBA"}
                                </span>
                              </div>
                            </motion.div>
                          )}
                          <motion.div
                            className="event-detail-item"
                            whileHover={{ x: 4 }}
                          >
                            <div className="event-detail-icon-wrapper">
                              <Users className="event-detail-icon" />
                            </div>
                            <div className="event-detail-content">
                              <span className="event-detail-label">Seats</span>
                              <span className="event-detail-value">
                                {event.number_of_seats} available
                              </span>
                            </div>
                          </motion.div>
                        </div>

                        <motion.button
                          className="event-view-more-btn"
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedEvent(event);
                            setIsModalOpen(true);
                            const original = eventSourceMap.get(
                              Number(event.id)
                            );
                            if (original) {
                              onGetTicket(original);
                            }
                          }}
                        >
                          <span>View Details</span>
                          <ArrowRight className="event-view-more-icon" />
                        </motion.button>
                      </div>

                      {/* Card Accent */}
                      <div className="event-card-accent"></div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              className="events-empty"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <Calendar className="events-empty-icon" />
              <p className="events-empty-text">
                {isLoadingEvents
                  ? "Loading events..."
                  : "No events available at the moment."}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="theme-two-contact">
        <div className="contact-container">
          <motion.div
            className="contact-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="contact-title">Get In Touch</h2>
          </motion.div>
          <div className="contact-items">
            {organization.website && (
              <motion.a
                href={organization.website}
                className="contact-item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                whileHover={{ x: 5 }}
              >
                <Mail className="contact-icon" />
                <span>{organization.website}</span>
              </motion.a>
            )}
            {organization.phone_number && (
              <motion.a
                href={`tel:${organization.phone_number}`}
                className="contact-item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                whileHover={{ x: 5 }}
              >
                <Phone className="contact-icon" />
                <span>{organization.phone_number}</span>
              </motion.a>
            )}
            {organization.website && (
              <motion.a
                href={organization.website}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                whileHover={{ x: 5 }}
              >
                <Globe className="contact-icon" />
                <span>Visit Website</span>
              </motion.a>
            )}
          </div>
          {socialLinks.length > 0 && (
            <motion.div
              className="contact-social"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Icon className="social-icon" />
                  </motion.a>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="theme-two-footer">
        <div className="footer-container">
          <div className="footer-content">
            {/* Brand Section */}
            <motion.div
              className="footer-brand"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {organization.logo && (
                <img
                  src={organization.logo}
                  alt={organization.name}
                  className="footer-logo"
                />
              )}
              <h3 className="footer-brand-name">{organization.name}</h3>
              {organization.subtitle && (
                <p className="footer-brand-tagline">{organization.subtitle}</p>
              )}
            </motion.div>

            {/* Quick Links */}
            <motion.div
              className="footer-section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="footer-section-title">Quick Links</h4>
              <ul className="footer-links">
                <li>
                  <a href="#about" className="footer-link">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#events" className="footer-link">
                    Events
                  </a>
                </li>
                <li>
                  <a href="#location" className="footer-link">
                    Location
                  </a>
                </li>
                <li>
                  <a href="#contact" className="footer-link">
                    Contact
                  </a>
                </li>
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              className="footer-section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="footer-section-title">Contact</h4>
              <ul className="footer-contact">
                {organization.website && (
                  <li className="footer-contact-item">
                    <Mail className="footer-contact-icon" />
                    <a
                      href={organization.website}
                      className="footer-contact-link"
                    >
                      {organization.website}
                    </a>
                  </li>
                )}
                {organization.phone_number && (
                  <li className="footer-contact-item">
                    <Phone className="footer-contact-icon" />
                    <a
                      href={`tel:${organization.phone_number}`}
                      className="footer-contact-link"
                    >
                      {organization.phone_number}
                    </a>
                  </li>
                )}
                {organization.website && (
                  <li className="footer-contact-item">
                    <Globe className="footer-contact-icon" />
                    <a
                      href={organization.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-contact-link"
                    >
                      Visit Website
                    </a>
                  </li>
                )}
                {organization.location && (
                  <li className="footer-contact-item">
                    <MapPin className="footer-contact-icon" />
                    <span className="footer-contact-text">
                      {organization.location.city},{" "}
                      {organization.location.state}
                    </span>
                  </li>
                )}
              </ul>
            </motion.div>

            {/* Social Links */}
            {socialLinks.length > 0 && (
              <motion.div
                className="footer-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <h4 className="footer-section-title">Follow Us</h4>
                <div className="footer-social">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <motion.a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-social-link"
                        whileHover={{ scale: 1.15, y: -4 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Icon className="footer-social-icon" />
                      </motion.a>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </div>

          {/* Copyright */}
          <motion.div
            className="footer-bottom"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <div className="footer-divider"></div>
            <p className="footer-copyright">
              © {new Date().getFullYear()} {organization.name}. All rights
              reserved.
            </p>
          </motion.div>
        </div>
      </footer>

      {/* Event Details Modal */}
      <AnimatePresence>
        {isModalOpen && selectedEvent && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="event-modal-overlay"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
              animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="event-modal"
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
              }}
            >
              <div className="event-modal-content">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="event-modal-close"
                >
                  <X className="event-modal-close-icon" />
                </button>

                {selectedEvent.cover_image && (
                  <div className="event-modal-image">
                    <img
                      src={selectedEvent.cover_image}
                      alt={selectedEvent.name}
                    />
                  </div>
                )}

                <div className="event-modal-body">
                  <div className="event-modal-header">
                    <div className="event-modal-badges">
                      <span className="event-modal-type-badge">
                        {selectedEvent.type}
                      </span>
                      <span
                        className={`event-modal-pricing-badge ${
                          selectedEvent.is_paid_event ? "paid" : "free"
                        }`}
                      >
                        {selectedEvent.is_paid_event
                          ? "Paid Event"
                          : "Free Event"}
                      </span>
                    </div>
                    <h2 className="event-modal-title">{selectedEvent.name}</h2>
                  </div>

                  <div className="event-modal-info">
                    <div className="event-modal-info-item">
                      <Calendar className="event-modal-info-icon" />
                      <div className="event-modal-info-content">
                        <span className="event-modal-info-label">Date</span>
                        <span className="event-modal-info-value">
                          {new Date(selectedEvent.date).toLocaleDateString(
                            "en-US",
                            {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </span>
                      </div>
                    </div>

                    {selectedEvent.location && (
                      <div className="event-modal-info-item">
                        <MapPin className="event-modal-info-icon" />
                        <div className="event-modal-info-content">
                          <span className="event-modal-info-label">
                            Location
                          </span>
                          <span className="event-modal-info-value">
                            {selectedEvent.location.address &&
                              `${selectedEvent.location.address}, `}
                            {selectedEvent.location.city &&
                              `${selectedEvent.location.city}, `}
                            {selectedEvent.location.state &&
                              `${selectedEvent.location.state}, `}
                            {selectedEvent.location.country}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="event-modal-info-item">
                      <Users className="event-modal-info-icon" />
                      <div className="event-modal-info-content">
                        <span className="event-modal-info-label">
                          Available Seats
                        </span>
                        <span className="event-modal-info-value">
                          {selectedEvent.number_of_seats} seats
                        </span>
                      </div>
                    </div>

                    {selectedEvent.host_name && (
                      <div className="event-modal-info-item">
                        <Award className="event-modal-info-icon" />
                        <div className="event-modal-info-content">
                          <span className="event-modal-info-label">Host</span>
                          <span className="event-modal-info-value">
                            {selectedEvent.host_name}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedEvent.event_description && (
                    <div className="event-modal-description">
                      <h3 className="event-modal-description-title">
                        About This Event
                      </h3>
                      <p className="event-modal-description-text">
                        {selectedEvent.event_description}
                      </p>
                    </div>
                  )}

                  <div className="event-modal-actions">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsModalOpen(false)}
                      className="event-modal-cancel-btn"
                    >
                      Cancel
                    </motion.button>
                    <motion.a
                      href={getEventUrl(
                        selectedEvent.id.toString(),
                        organization.slug
                      )}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="event-modal-view-btn"
                    >
                      <span>View Full Details</span>
                      <ArrowRight className="event-modal-view-icon" />
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export { OrganizationHomepageThemeTwo };
export default OrganizationHomepageThemeTwo;
